import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UcatLayout } from "../../Components/Shared";
import { Button } from "../../Components/Practice_Test/UI";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { ReactComponent as InfoSvg } from "../../Assests/icons8-info (1).svg";
import {
  FileText,
  SquarePlus,
  SquareMinus,
  LogOut,
  Flag,
  PenLine,
  Info,
  CheckCircle,
  XCircle
} from "lucide-react";
import { listReViewExamIDPractice } from "../../../../Redux/Slices/ReViewExamPractice/ListReViewExamPracticeSlice";
import { listReViewExamID } from "../../../../Redux/Slices/ReViewExam/ListReViewExamSlice";
import { listEvaluation } from "../../../../Redux/Slices/Exam/ListEvaluationSlice";
import { listEvaluationPractice } from "../../../../Redux/Slices/PracticeExam/ListEvaluationPracticeSlice";
import { CircularProgress } from "@mui/material";

export const UcatResultScreen = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showInstructions, setShowInstructions] = useState(false);
  const [isReviewState, setIsReviewState] = useState(true);

  // Get student_test_id from ListStudentExamSlice for practice tests
  const { data: listStudentExamSlice } = useAppSelector(
    (state) => state.ListStudentExamSlice
  );

  const { data: listReViewExamIDSlice, isLoading: Reviewdata } = useAppSelector(
    (state) => state.ListReViewExamIDSlice || {}
  );

  const { data: listReViewExamPracticeSlice, isLoading: ReviewdataPractice } = useAppSelector(
    (state: any) => state.ListReViewExamIDPracticeSlice || {}
  );

  const { data: listEvaluationSlice, isLoading: LoadingEval } = useAppSelector(
    (state) => state.ListEvaluationSlice || {}
  );

  const { data: listEvaluationPracticeSlice, isLoading: LoadingEvalPractice } = useAppSelector(
    (state) => state.ListEvaluationPracticeSlice || {}
  );

  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]));

  // Log the entire location state for debugging
  console.log("🌍 Location state received:", state);

  // Persist full state to sessionStorage to handle reloads
  useEffect(() => {
    if (state) {
      sessionStorage.setItem("ucat_last_result_state", JSON.stringify(state));
    }
  }, [state]);

  const rawPersistedState = sessionStorage.getItem("ucat_last_result_state");
  const persistedState = rawPersistedState ? JSON.parse(rawPersistedState) : null;

  const localResults = state || persistedState || {
    total: 0,
    correct: 0,
    sectionTitle: "Exam Result",
    questionsByType: [],
    studentExamId: null
  };

  // For practice tests, use id from ListStudentExamSlice
  const studentTestId = (listStudentExamSlice as any)?.data?.id || null;

  const idToUse = localResults.studentExamId || studentTestId;
  const evaluationTriggered = React.useRef(false);
  const [evalLoadHasStarted, setEvalLoadHasStarted] = useState(false);

  useEffect(() => {
    if (idToUse && !evaluationTriggered.current) {
      console.log("🔍 Starting evaluation sequence for id:", idToUse);
      dispatch(listEvaluation({ student_exam_id: idToUse }));
      evaluationTriggered.current = true;
    }
  }, [dispatch, idToUse]);

  useEffect(() => {
    if (LoadingEval === true) {
      setEvalLoadHasStarted(true);
    }
  }, [LoadingEval]);

  useEffect(() => {
    const hasEvalData = listEvaluationSlice && (Array.isArray(listEvaluationSlice) ? listEvaluationSlice.length > 0 : !!listEvaluationSlice);

    if (idToUse && evaluationTriggered.current && LoadingEval === false && hasEvalData && !listReViewExamIDSlice && !Reviewdata) {
      console.log("✅ Evaluation successful/ready, now fetching review data for id:", idToUse);
      dispatch(listReViewExamID({ id: idToUse }));
    }
  }, [dispatch, idToUse, LoadingEval, listEvaluationSlice, listReViewExamIDSlice, Reviewdata, evalLoadHasStarted]);

  const sectionTitle = listReViewExamIDSlice?.data?.exam_name || "UCAT Exam";

  const handleEndReview = () => {
    navigate("/ucat_test");
  };

  const toggleSection = (index: number) => {
    const next = new Set(expandedSections);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    setExpandedSections(next);
  };

  // Navigate to review exam page with the student exam ID
  const handleReviewQuestion = (questionId: number, questionIdxInFiltered?: number) => {
    const idToUse = localResults.studentExamId || studentTestId;
    if (idToUse) {
      navigate("/ucat_test_review", {
        state: {
          studentExamId: idToUse,
          filterType: "all",
          initialQuestionIndex: questionIdxInFiltered || 0
        }
      });
    }
  };

  const handleReviewAll = () => {
    const idToUse = localResults.studentExamId || studentTestId;
    if (idToUse) {
      navigate("/ucat_test_review", {
        state: {
          studentExamId: idToUse,
          filterType: "all",
          initialQuestionIndex: 0
        }
      });
    }
  };

  const handleReviewIncorrect = () => {
    const idToUse = localResults.studentExamId || studentTestId;
    if (idToUse) {
      navigate("/ucat_test_review", {
        state: {
          studentExamId: idToUse,
          filterType: "incorrect",
          initialQuestionIndex: 0
        }
      });
    }
  };

  const handleReviewFlagged = () => {
    const idToUse = localResults.studentExamId || studentTestId;
    if (idToUse) {
      navigate("/ucat_test_review", {
        state: {
          studentExamId: idToUse,
          filterType: "flagged",
          initialQuestionIndex: 0
        }
      });
    }
  };

  // Extract data from API response
  const reviewDataRaw = listReViewExamIDSlice?.data || listReViewExamPracticeSlice?.data || listReViewExamIDSlice || listReViewExamPracticeSlice;
  const reviewData: any = reviewDataRaw;
  const allQuestions = reviewData?.questions || [];

  // Group questions by type
  const questionsByType: Record<string, any[]> = {};
  allQuestions.forEach((q: any) => {
    const typeName = q.question_type_name || "General";
    if (!questionsByType[typeName]) {
      questionsByType[typeName] = [];
    }
    questionsByType[typeName].push(q);
  });

  const findQuestionsLine = (obj: any): any[] => {
    if (!obj) return [];
    if (Array.isArray(obj)) {
      if (obj[0]?.questions_line) return obj[0].questions_line;
      if (obj.length > 0 && obj[0]?.question_id) return obj;
      return [];
    }
    if (obj.questions_line) return obj.questions_line;
    if (obj.data?.questions_line) return obj.data.questions_line;
    if (obj.data) {
      if (Array.isArray(obj.data)) return obj.data[0]?.questions_line || [];
      if (obj.data.questions_line) return obj.data.questions_line;
      return findQuestionsLine(obj.data);
    }
    return [];
  };

  const evalLinesMock = findQuestionsLine(listEvaluationSlice);
  const evalLinesPractice = findQuestionsLine(listEvaluationPracticeSlice);
  const evaluationLines = evalLinesMock.length > 0 ? evalLinesMock : evalLinesPractice;

  const evaluationMap = new Map();
  evaluationLines.forEach((item: any) => {
    evaluationMap.set(Number(item.question_id), item);
  });

  // Create subsections
  const subsections = Object.entries(questionsByType).map(([typeName, questions], sectionIdx) => {
    const questionStatuses = questions.map((q: any) => {
      const evalItem = evaluationMap.get(Number(q.question_id));
      const isFlagged = q.flagged === true || q.isFlagged === true || q.is_flagged === true || q.is_flagged === 1 || q.is_flagged === "true";

      let status = "Unseen";
      let isCorrect = false;

      // Prioritize Evaluation API data
      if (evalItem) {
        const hasServerAnswer = evalItem.option_id !== null ||
          (evalItem.answer_data && evalItem.answer_data !== "null" && evalItem.answer_data !== "{}") ||
          (evalItem.sentence && evalItem.sentence !== "null" && evalItem.sentence !== "{}");
        const isSkipped = evalItem.skipped === true || evalItem.skipped === "true" || evalItem.skipped === 1;

        if (isSkipped) {
          status = "Unseen";
        } else if (hasServerAnswer) {
          isCorrect = evalItem.correct === true || evalItem.correct === "true" || evalItem.correct === 1;
          status = isCorrect ? "Correct" : "Incorrect";
        } else if (evalItem.time_taken_seconds && Number(evalItem.time_taken_seconds) > 0) {
          status = "Incomplete";
        } else {
          status = "Unseen";
        }
      } else {
        // Fallback to review API data if evaluation data is missing
        const chosenOption = q.options?.find((opt: any) => opt.chosen);
        if (chosenOption) {
          isCorrect = !!chosenOption.is_correct;
          status = isCorrect ? "Correct" : "Incorrect";
        } else if (q.answer_items && Array.isArray(q.answer_items) && q.answer_items.some((item: any) => item.value)) {
          status = "Incorrect"; // It was answered but we don't know correctness without evalItem
        }
      }

      return {
        question_id: q.question_id,
        status: status,
        isFlagged: isFlagged,
        isCorrect: isCorrect
      };
    });

    const incorrectCount = questionStatuses.filter(qs => qs.status === "Incorrect" || qs.status === "Incomplete" || qs.status === "Unseen").length;

    return {
      name: typeName,
      count: questions.length,
      incorrect: incorrectCount,
      questions: questionStatuses
    };
  });

  const getStatusColor = (status: string) => {
    const s = status.toLowerCase();
    if (s === "correct") return "text-green-600";
    if (s === "incorrect" || s === "unseen" || s === "wrong" || s === "incomplete") return "text-[#E00]";
    return "text-gray-500";
  };

  const formatStatus = (status: string) => {
    if (!status) return "Unseen";
    const s = status.toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const isLoading = Reviewdata || ReviewdataPractice || LoadingEval || LoadingEvalPractice;

  return (
    <>
      <UcatLayout
        headerTitle={sectionTitle}
        showToolbar={true}
        toolbarLeftActions={
          isReviewState && (
            <button
              onClick={() => setShowInstructions(true)}
              className="flex items-center gap-2 text-white hover:text-[#FFFF00] text-[14px] font-medium p-1 ml-2"
            >
              <PenLine size={16} />
              <span className="text-[18px] underline decoration-1 f-tahoma">
                Instructions
              </span>
            </button>
          )
        }
        footerProps={{
          onEndExam: handleEndReview,
          leftButtonText: "End Review",
          customRightActions: (
            <>
              <Button
                variant="pearson-nav"
                className="flex items-center gap-1.5 px-4 text-[16px] h-[100%]"
                onClick={handleReviewAll}
              >
                <CheckCircle size={18} /> Review All
              </Button>
              <Button
                variant="pearson-nav"
                className="flex items-center gap-1.5 px-4 text-[16px] h-[100%]"
                onClick={handleReviewIncorrect}
              >
                <XCircle size={18} /> Review Incorrect
              </Button>
              <Button
                variant="pearson-nav"
                className="flex items-center gap-1.5 px-4 text-[16px] h-[100%]"
                onClick={handleReviewFlagged}
              >
                <Flag size={18} className="fill-white" /> Review Flagged
              </Button>
            </>
          )
        }}
      >
        <div className="flex-1 h-0 min-h-0 w-full overflow-y-auto p-0 flex flex-col items-center bg-gray-50/50 ucat-scrollbar">
          <div className="p-1 flex items-center gap-2 text-[#000]-700 w-full sticky top-0 z-10">
            <div className="flex-1 text-center text-[19px] font-[400] pr-10 mb-[10px] f-tahoma">
              Postscore: see which items are incorrect and return to them to see the solution
            </div>
          </div>

          <div className="w-full p-4 space-y-4 max-w-full">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white w-full">
                <CircularProgress />
                <p className="mt-3 text-[#005a9c] font-medium">Loading results...</p>
              </div>
            ) : subsections.length > 0 ? (
              subsections.map((sub: any, index: number) => {
                const isExpanded = expandedSections.has(index);
                return (
                  <div key={index} className="flex flex-col">
                    <div
                      onClick={() => toggleSection(index)}
                      className="flex items-center justify-between px-4 py-1 bg-[#507abd] text-white hover:bg-[#4068a0] transition-colors cursor-pointer rounded-sm"
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? <SquareMinus size={18} className="text-white/90" /> : <SquarePlus size={18} strokeWidth={3} className="text-white/90" />}
                        <span className="text-[19px] font-medium">
                          Question Bank Section {index + 1} - {sub.name}
                        </span>
                      </div>
                      <div className="text-[19px] font-medium tracking-wide">
                        ( {sub.count} Questions , {sub.incorrect} Incorrect )
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="bg-white border-x border-gray-200">
                        {sub.questions.length > 0 ? (
                          <div className="divide-y divide-gray-100">
                            {sub.questions.map((q: any, qIdx: number) => (
                              <div
                                key={q.question_id}
                                onClick={() => handleReviewQuestion(q.question_id, allQuestions.findIndex((aq: any) => aq.question_id === q.question_id))}
                                className="flex items-center justify-between px-6 py-1.5 hover:bg-blue-50/30 transition-colors cursor-pointer group"
                              >
                                <div className="flex items-center gap-4">
                                  {q.isFlagged ? (
                                    <Flag size={18} className="fill-orange-400 text-orange-400" />
                                  ) : (
                                    <PenLine size={16} className="text-blue-400 group-hover:text-blue-600" />
                                  )}
                                  <span className="text-[16px] text-gray-700">Question {qIdx + 1}</span>
                                </div>
                                <span className={`text-[16px] font-normal ${getStatusColor(q.status)}`}>
                                  {formatStatus(q.status)}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="px-10 py-4 text-[13px] text-gray-400 italic">
                            No detailed question results available.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="w-full py-10 text-center text-gray-500">
                No review data available. Please complete the exam first.
              </div>
            )}
          </div>
        </div>
      </UcatLayout>
      {/* Instructions Modal - Pearson Style */}
      {
        showInstructions && (
          <div className="fixed inset-0 flex items-center justify-center z-[10000] bg-black/40">
            <div className="bg-[#005a9c] w-full max-w-5xl border border-white shadow-2xl overflow-hidden">
              {/* Title Bar */}
              <div className="text-white px-3 py-1.5 border-b border-white/30 flex items-center f-tahoma">
                <h2 className="text-[16px] font-normal">Review Instructions</h2>
              </div>

              <div className="p-4 text-white font-[inherit]">
                <div className="flex items-start gap-4 mb-4">
                  {/* Info Icon */}
                  <div className="flex-shrink-0 mt-1">
                    <InfoSvg className="w-[48px] h-[48px] text-[#005a9c] fill-white" />
                  </div>

                  <div className="text-[16px] leading-relaxed space-y-6">
                    <p className="text-[16px] f-tahoma">
                      Below is a summary of your answers. You can review your questions in three (3) different ways.
                    </p>

                    <div>
                      <p className="mb-4 text-[16px] f-tahoma">
                        The buttons in the lower right-hand corner correspond to these choices:
                      </p>
                      <div className="space-y-1 ml-1 text-[16px] f-tahoma">
                        <p className="text-[16px] f-tahoma">1. Review all of your questions and answers.</p>
                        <p className="text-[16px] f-tahoma">2. Review questions that are incomplete.</p>
                        <p className="text-[16px] f-tahoma">3. Review questions that are flagged for review. (Click the 'flag' icon to change the flag for review status.)</p>
                      </div>
                    </div>

                    <p className="text-[16px] f-tahoma">
                      You may also click on a question number to link directly to its location in the exam.
                    </p>
                  </div>
                </div>

                {/* Close Button */}
                <div className="flex justify-center mt-12 mb-2">
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="px-2 py-1 border border-white text-white font-700 hover:bg-[#FFFF00] hover:text-[#005a9c] transition-colors text-[18px]"
                  >
                    <span className="f-tahoma underline">O</span>K
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};

export default UcatResultScreen;
